import { useEffect, useRef } from 'react';

interface HeatWavesProps {
  isDarkMode: boolean;
}

const HeatWaves = ({ isDarkMode }: HeatWavesProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    let animationId: number;
    let time = 0;

    const draw = () => {
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);

      time += 0.01;

      const numWaves = 5;
      const waveSpacing = height / (numWaves + 1);

      for (let i = 1; i <= numWaves; i++) {
        const baseY = i * waveSpacing;
        const amplitude = 15 + i * 3;
        const frequency = 0.005 + i * 0.001;

        const gradient = ctx.createLinearGradient(0, baseY - 20, 0, baseY + 20);
        gradient.addColorStop(0, `rgba(255, 140, 0, 0)`);
        gradient.addColorStop(0.5, `rgba(255, 100, 50, ${0.1 + i * 0.02})`);
        gradient.addColorStop(1, `rgba(255, 140, 0, 0)`);

        ctx.strokeStyle = gradient;
        ctx.lineWidth = 2;
        ctx.beginPath();

        for (let x = 0; x <= width; x += 5) {
          const y = baseY + Math.sin(x * frequency + time + i) * amplitude;
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }

        ctx.stroke();
      }

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, [isDarkMode]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        opacity: 0.25,
      }}
    />
  );
};

export default HeatWaves;
